/**
 * The top bar of every page outside a game (03 §0.4): the main links on the left, the account on the right (the
 * avatar and display name, opening a menu), or "Sign in" for a guest. On a phone the links fold into a "Menu"
 * button.
 */
import { type MsgKey, t } from '@tarock/i18n';
import type { SiteId } from '@tarock/protocol/sites';
import { useEffect, useRef, useState } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import { publicConfig } from '../auth/config';
import { authStore, signOut } from '../stores/auth';
import { Avatar } from './Avatar';
import { immersive } from './chrome';
import { FEATURES } from './features';
import { SITE, site } from './site';

/** A main link: where it goes, its label, whether it needs a signed-in user, and the sites that have it. */
interface NavLink {
  href: string;
  key: MsgKey;
  user: boolean;
  sites: readonly SiteId[];
}

const BOTH: readonly SiteId[] = ['tarock', 'tile'];
// 11 §8.3: leaderboards and history are per game (Q6); tile.game has its own since its first game's records (P16)
const LINKS: readonly NavLink[] = [
  { href: '/', key: 'nav.home', user: false, sites: BOTH },
  { href: '/lobby', key: 'nav.lobby', user: true, sites: BOTH },
  ...(FEATURES.leaderboard ? [{ href: '/leaderboard', key: 'nav.leaderboard', user: true, sites: BOTH } as const] : []),
  { href: '/history', key: 'nav.history', user: true, sites: BOTH },
  { href: '/play/local', key: 'nav.playLocal', user: false, sites: ['tarock'] },
];

/** True when `path` is `href` or below it (`/` only matches itself). */
function isCurrent(path: string, href: string): boolean {
  return href === '/' ? path === '/' : path === href || path.startsWith(`${href}/`);
}

/** Closes a disclosure on Escape and on a click outside `ref`. */
function useDismiss(open: boolean, close: () => void, ref: { current: HTMLElement | null }): void {
  useEffect(() => {
    if (!open) {
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        close();
      }
    };
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        close();
      }
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('click', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('click', onClick);
    };
  }, [open]);
}

/** The account menu: Edit profile, Invite players (when the flag is on), Statistics, Security, Sign out. */
function AccountMenu() {
  const user = authStore.user.value;
  const { path } = useLocation();
  const [open, setOpen] = useState(false);
  const [invites, setInvites] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useDismiss(open, () => setOpen(false), box);
  useEffect(() => setOpen(false), [path]);
  useEffect(() => {
    void publicConfig().then((c) => setInvites(c?.playerInvites ?? false));
  }, []);
  if (!user) {
    return null;
  }
  const items: [string, MsgKey][] = [
    ['/profile', 'nav.editProfile'],
    ...(invites ? [['/profile/invites', 'invites.title'] as [string, MsgKey]] : []),
    ['/profile/stats', 'stats.title'],
    ['/profile/security', 'security.title'],
  ];
  return (
    <div class="account" ref={box}>
      <button
        type="button"
        class="account-button"
        aria-expanded={open}
        aria-controls="account-menu"
        aria-label={t('nav.accountMenu', { name: user.displayName })}
        data-testid="account-menu-button"
        onClick={() => setOpen(!open)}
      >
        <Avatar url={user.avatarUrl} name={user.displayName} id={user.id} size={30} />
        <span class="account-name">{user.displayName}</span>
        <span class="caret" aria-hidden="true">
          ▾
        </span>
      </button>
      {open && (
        <ul class="account-menu" id="account-menu" data-testid="account-menu">
          {items.map(([href, key]) => (
            <li key={href}>
              <a href={href} aria-current={path === href ? 'page' : undefined}>
                {t(key)}
              </a>
            </li>
          ))}
          <li>
            <button type="button" onClick={() => void signOut()}>
              {t('nav.signOut')}
            </button>
          </li>
        </ul>
      )}
    </div>
  );
}

/** The top bar; nothing while a table fills the screen. */
export function SiteHeader() {
  const { path } = useLocation();
  const [menu, setMenu] = useState(false);
  const bar = useRef<HTMLDivElement>(null);
  useDismiss(menu, () => setMenu(false), bar);
  useEffect(() => setMenu(false), [path]);
  if (immersive.value) {
    return null;
  }
  const status = authStore.status.value;
  const user = authStore.user.value;
  const links = LINKS.filter((l) => (!l.user || user) && l.sites.includes(SITE));
  return (
    <header class="site-header" data-testid="site-header">
      <div class="site-bar" ref={bar}>
        <a class="brand" href="/">
          {site.name}
        </a>
        <button
          type="button"
          class="menu-button"
          aria-expanded={menu}
          aria-controls="main-nav"
          data-testid="menu-button"
          onClick={() => setMenu(!menu)}
        >
          {t('nav.menu')}
        </button>
        <nav class={`main-nav${menu ? ' open' : ''}`} id="main-nav" aria-label={t('nav.main')}>
          {links.map((l) => (
            <a key={l.href} href={l.href} aria-current={isCurrent(path, l.href) ? 'page' : undefined}>
              {t(l.key)}
            </a>
          ))}
          {user?.role === 'admin' && (
            <a href="/admin" aria-current={isCurrent(path, '/admin') ? 'page' : undefined}>
              {t('nav.admin')}
            </a>
          )}
        </nav>
        <div class="site-account">
          {user ? (
            <AccountMenu />
          ) : status === 'unknown' || path === '/login' ? null : (
            <a class="btn small" href="/login">
              {t('nav.signIn')}
            </a>
          )}
        </div>
      </div>
    </header>
  );
}
